import { format } from 'date-fns'

import { CalendarSource } from '../../../constants/calendar'
import { CalendarGridMode } from '../../../constants/calendarGrid'
import { CHARACTER_WHITESPACE, EMPTY_LIST } from '../../../constants/common'
import { DATE_API_DATA_FORMAT_SHORT } from '../../../constants/date'
import { getCalendarGridExpandedItems } from '../../CalendarGrid'

import { CALENDAR_VIEW_NO_TASK_TYPES_COUNT } from './CalendarView.constants'

function getIsCalendarViewTaskTypesEmpty(calendar) {
  return (
    calendar.settingsHint.taskTypesEnabled === CALENDAR_VIEW_NO_TASK_TYPES_COUNT
  )
}

function getIsCalendarViewNoTaskTypes({ calendar, filters }) {
  const hasTaskDeadlines = filters.sources.includes(CalendarSource.taskDeadline)

  return hasTaskDeadlines && getIsCalendarViewTaskTypesEmpty(calendar)
}

function getIsCalendarViewEmpty(calendar) {
  return (
    !calendar.isLoading && !calendar.isRefetching && calendar.items.length === 0
  )
}

function getCalendarViewEmptyRangeText({ calendar, t }) {
  switch (calendar.mode) {
    case CalendarGridMode.agenda:
      return t('Calendar.EmptyAgendaKey')
    case CalendarGridMode.day:
      return t('Calendar.EmptyDayKey')
    default:
      return t('Calendar.EmptyRangeKey')
  }
}

function getCalendarViewNoTaskTypesMessage({
  canOpenSettings,
  t,
  onOpenSettings,
}) {
  const text = t('Calendar.EmptyNoTaskTypesKey')

  return canOpenSettings
    ? {
        action: {
          callback: onOpenSettings,
          text: t('Calendar.EmptyNoTaskTypesActionKey'),
        },
        text,
      }
    : {
        text: [text, t('Calendar.EmptyNoTaskTypesManagerKey')].join(
          CHARACTER_WHITESPACE
        ),
      }
}

function getCalendarViewEmptyMessage({
  calendar,
  canOpenSettings,
  filters,
  t,
  onOpenSettings,
}) {
  if (filters.sources.length === 0) {
    return { text: t('Calendar.EmptyNoSourceKey') }
  }

  return getIsCalendarViewNoTaskTypes({ calendar, filters })
    ? getCalendarViewNoTaskTypesMessage({ canOpenSettings, t, onOpenSettings })
    : { text: getCalendarViewEmptyRangeText({ calendar, t }) }
}

function getCalendarViewEntryItems(entry) {
  return entry.card?.steps ?? [entry]
}

export function getHasCalendarViewNoTaskTypesNotice({ calendar, filters }) {
  return (
    calendar.items.length > 0 &&
    !calendar.serverError &&
    getIsCalendarViewNoTaskTypes({ calendar, filters })
  )
}

export function getHasCalendarViewAppointments(filters) {
  return filters.sources.includes(CalendarSource.appointment)
}

export function getCalendarViewNoTaskTypesNoticeProps({
  canOpenSettings,
  t,
  onOpenSettings,
}) {
  const { action, text } = getCalendarViewNoTaskTypesMessage({
    canOpenSettings,
    t,
    onOpenSettings,
  })

  return { actions: action ? [action] : EMPTY_LIST, text }
}

export function getCalendarViewBodyMessage({
  calendar,
  canOpenSettings,
  filters,
  t,
  onOpenSettings,
}) {
  if (calendar.serverError) {
    return {
      action: { callback: calendar.handleReload, text: t('Calendar.RetryKey') },
      text: t('Calendar.ErrorLoadKey'),
    }
  }

  if (!getIsCalendarViewEmpty(calendar)) {
    return null
  }

  return getCalendarViewEmptyMessage({
    calendar,
    canOpenSettings,
    filters,
    t,
    onOpenSettings,
  })
}

export function getCalendarViewRequestKey({ filters, today }) {
  return JSON.stringify({
    filters,
    today: format(today, DATE_API_DATA_FORMAT_SHORT),
  })
}

export function getIsCalendarViewItemChangedMeanwhile({ date, item, items }) {
  const storedItem = getCalendarGridExpandedItems(items)
    .flatMap(getCalendarViewEntryItems)
    .find(entry => entry.id === item.id)

  return Boolean(storedItem) && storedItem.date !== date
}
